import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Edit2, Trash2, FlaskConical, Package, Check, X, Banknote } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ClickToPreviewImage from '@/components/ui/ClickToPreviewImage';
import { needsLiterConversion, formatGramMl } from '@/lib/unitConverter';
import { base44 } from '@/api/base44Client';

export default function VariantSizeAccordion({
  sizeData,
  activeBrandName,
  variantName,
  canEdit,
  isMarketing,
  fileRef,
  onEditVariant,
  onDeleteRacikan,
  onDeleteBahan
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingJasa, setEditingJasa] = useState(false);
  const [editingHargaJual, setEditingHargaJual] = useState(false);
  const [editingHpp, setEditingHpp] = useState(false);
  const [customJasa, setCustomJasa] = useState(sizeData.bahan?.custom_jasa || '');
  const [customMargin, setCustomMargin] = useState(sizeData.bahan?.custom_margin ?? '');
  const [customHpp, setCustomHpp] = useState(sizeData.bahan?.custom_hpp || '');
  const [isSavingJasa, setIsSavingJasa] = useState(false);
  const [isSavingHargaJual, setIsSavingHargaJual] = useState(false);
  const [isSavingHpp, setIsSavingHpp] = useState(false);

  // Fetch Master Data
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: bahanList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: itemTambahanList = [] } = useQuery({ queryKey: ['item-tambahan'], queryFn: () => base44.entities.ItemTambahan.list() });

  const getHargaJual = (item) => item?.harga_jual || (item?.harga_rupiah ? item.harga_rupiah * 1.3 : 0);
  const getHargaBeli = (item) => item?.harga_beli || item?.harga_rupiah || 0;

  // Fetch fullset list for price calculation
  const { data: fullsetListPrice = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });

  // Calculate default values based on items
  const { defaultJasa, defaultHargaTotal, modalFisikTotal, modalKemasanUtama, modalItemTambahan, modalRacikanFinal, hppRacikanFinal, hppKemasan, hppKemasanUtama, hppItemTambahan } = useMemo(() => {
    let defaultHppRacikan = 0;
    let defaultModalRacikan = 0;
    const ukuran_ml = (sizeData.bahan?.botol_ukuran_aktual_ml ? Number(sizeData.bahan.botol_ukuran_aktual_ml) : (Number(sizeData.bahan?.botol_ukuran_label_ml) || Number(sizeData.racikan?.ukuran_ml) || 0));

    if (sizeData.racikan?.racikan?.length) {
      sizeData.racikan.racikan.forEach(r => {
        const bahanInfo = bahanList.find(b => (b.nama || '').trim().toLowerCase() === (r.nama_bahan || '').trim().toLowerCase());
        if (bahanInfo) {
          const hppPerUnit = getHargaJual(bahanInfo);
          const modalPerUnit = getHargaBeli(bahanInfo);
          const volMl = ukuran_ml > 0 ? ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100) * ukuran_ml : (r.volume_ml || 0);
          defaultHppRacikan += (hppPerUnit * volMl);
          defaultModalRacikan += (modalPerUnit * volMl);
        }
      });
    }

    let defaultHppKemasanUtama = 0;
    let defaultModalKemasanUtama = 0;
    let defaultHppItemTambahan = 0;
    let defaultModalItemTambahan = 0;

    if (sizeData.bahan) {
      // Check if this product uses fullset packaging
      if (sizeData.bahan.is_fullset && sizeData.bahan.fullset_nama) {
        const fullsetMatch = fullsetListPrice.find(x => (x.nama || '').trim().toLowerCase() === (sizeData.bahan.fullset_nama || '').trim().toLowerCase());
        if (fullsetMatch) {
          defaultHppKemasanUtama += getHargaJual(fullsetMatch);
          defaultModalKemasanUtama += getHargaBeli(fullsetMatch);
        }
      } else {
        const b = botolList.find(x => (x.nama || '').trim().toLowerCase() === (sizeData.bahan.botol_nama || '').trim().toLowerCase());
        if (b) {
          defaultHppKemasanUtama += getHargaJual(b);
          defaultModalKemasanUtama += getHargaBeli(b);
        }

        const t = tutupList.find(x => (x.nama || '').trim().toLowerCase() === (sizeData.bahan.tutup_nama || '').trim().toLowerCase());
        if (t) {
          defaultHppKemasanUtama += getHargaJual(t);
          defaultModalKemasanUtama += getHargaBeli(t);
        }

        const s = sprayList.find(x => (x.nama || '').trim().toLowerCase() === (sizeData.bahan.spray_nama || '').trim().toLowerCase());
        if (s) {
          defaultHppKemasanUtama += getHargaJual(s);
          defaultModalKemasanUtama += getHargaBeli(s);
        }
      }

      // Check for additional items
      if (Array.isArray(sizeData.bahan.item_tambahan)) {
        sizeData.bahan.item_tambahan.forEach(it => {
          const matched = itemTambahanList.find(x => (x.nama || '').trim().toLowerCase() === (it.nama || '').trim().toLowerCase());
          if (matched) {
            defaultHppItemTambahan += (getHargaJual(matched) * (it.qty_per_pcs || 1));
            defaultModalItemTambahan += (getHargaBeli(matched) * (it.qty_per_pcs || 1));
          }
        });
      }
    }
    
    const getJasaProduksi = (ukuran) => {
      if (ukuran === 0) return 0;
      if (ukuran <= 35) return 3000;
      if (ukuran <= 105) return 4000;
      if (ukuran <= 255) return 5000;
      return 6000;
    };
    
    const jasa = getJasaProduksi(ukuran_ml);
    const hppRacikanFinal = defaultHppRacikan / 1000; 
    const modalRacikanFinal = defaultModalRacikan / 1000;

    const defaultHppKemasan = defaultHppKemasanUtama + defaultHppItemTambahan;
    const defaultModalKemasan = defaultModalKemasanUtama + defaultModalItemTambahan;
    const total = hppRacikanFinal + defaultHppKemasan + jasa;
    const modalTotal = modalRacikanFinal + defaultModalKemasan;

    return {
      defaultJasa: jasa,
      defaultHargaTotal: total,
      modalFisikTotal: modalTotal,
      modalKemasanUtama: defaultModalKemasanUtama,
      modalItemTambahan: defaultModalItemTambahan,
      modalRacikanFinal: modalRacikanFinal,
      hppRacikanFinal: hppRacikanFinal,
      hppKemasan: defaultHppKemasan,
      hppKemasanUtama: defaultHppKemasanUtama,
      hppItemTambahan: defaultHppItemTambahan
    };
  }, [sizeData, botolList, tutupList, sprayList, bahanList, itemTambahanList, fullsetListPrice]);

  const handleSaveJasa = async () => {
    if (!sizeData.bahan?.id) {
      alert('Simpan Kemasan/Packaging terlebih dahulu sebelum mengatur harga custom.');
      return;
    }
    setIsSavingJasa(true);
    try {
      await base44.entities.DatabaseBahan.update(sizeData.bahan.id, { 
        custom_jasa: customJasa ? Number(customJasa) : null
      });
      setEditingJasa(false);
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert('Gagal menyimpan harga kustom.');
    } finally {
      setIsSavingJasa(false);
    }
  };

  const handleSaveHargaJual = async () => {
    if (!sizeData.bahan?.id) {
      alert('Simpan Kemasan/Packaging terlebih dahulu sebelum mengatur harga custom.');
      return;
    }
    setIsSavingHargaJual(true);
    try {
      const finalHppVal = (sizeData.bahan?.custom_hpp !== undefined && sizeData.bahan?.custom_hpp !== null) 
        ? sizeData.bahan.custom_hpp 
        : ((hppKemasan || 0) + (hppRacikanFinal || 0));
      const finalJasaVal = (sizeData.bahan?.custom_jasa !== undefined && sizeData.bahan?.custom_jasa !== null)
        ? sizeData.bahan.custom_jasa
        : defaultJasa;
        
      const finalMarginVal = (customHargaJual !== '' && customHargaJual !== null && customHargaJual !== undefined)
        ? (Number(customHargaJual) - (finalHppVal + finalJasaVal))
        : (customMargin !== '' && customMargin !== null && customMargin !== undefined ? Number(customMargin) : null);

      const finalHargaJualVal = (customHargaJual !== '' && customHargaJual !== null && customHargaJual !== undefined)
        ? Number(customHargaJual)
        : (finalMarginVal !== null ? (finalHppVal + finalJasaVal + finalMarginVal) : null);

      const updatePayload = {
        ...sizeData.bahan,
        custom_margin: finalMarginVal !== null ? Math.round(finalMarginVal) : null,
        custom_harga_jual: finalHargaJualVal !== null ? Math.round(finalHargaJualVal) : null
      };

      await base44.entities.DatabaseBahan.update(sizeData.bahan.id, updatePayload);
      setEditingHargaJual(false);
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert('Gagal menyimpan harga kustom.');
    } finally {
      setIsSavingHargaJual(false);
    }
  };

  const handleSaveHpp = async () => {
    if (!sizeData.bahan?.id) {
      alert('Simpan Kemasan/Packaging terlebih dahulu sebelum mengatur harga custom.');
      return;
    }
    setIsSavingHpp(true);
    try {
      await base44.entities.DatabaseBahan.update(sizeData.bahan.id, { 
        custom_hpp: customHpp ? Number(customHpp) : null
      });
      setEditingHpp(false);
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert('Gagal menyimpan harga kustom.');
    } finally {
      setIsSavingHpp(false);
    }
  };

  const handleDirectUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !sizeData.bahan?.id) return;

    setUploadingImage(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      await base44.entities.DatabaseBahan.update(sizeData.bahan.id, { image_url: file_url });
      // Reload is simpler than passing mutation props all the way down just for this inline feature
      window.location.reload(); 
    } catch (err) {
      console.error(err);
      alert('Gagal mengunggah gambar.');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const checkBahanInInventory = (r) => {
    if (r.dari_client || (r.vendor && r.vendor.toLowerCase() === 'client')) {
      return { inMaster: true, isClient: true };
    }
    const namaBahan = r.nama_bahan || r.bahan_nama || '';
    if (!namaBahan) return { inMaster: false };
    const normalize = (str) => (str || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
    let matched = bahanList.find(x => (x.nama || '').trim().toLowerCase() === namaBahan.trim().toLowerCase());
    if (!matched) {
      const rcNorm = normalize(namaBahan);
      matched = bahanList.find(x => normalize(x.nama) === rcNorm);
    }
    if (!matched) {
      const clean = namaBahan.trim().toLowerCase();
      if (clean.length >= 4) {
        matched = bahanList.find(x => {
          const xName = (x.nama || '').trim().toLowerCase();
          return xName.startsWith(clean) || clean.startsWith(xName) || xName.includes(clean);
        });
      }
    }
    if (!matched) {
      const stdCategories = ['dpg', 'aqua des', 'alkohol', 'peg', 'sustain'];
      const catLower = (r.kategori_bahan || r.kategori || '').trim().toLowerCase();
      if (stdCategories.includes(catLower)) {
        matched = bahanList.find(x => (x.kategori || '').trim().toLowerCase() === catLower);
      }
    }
    return { inMaster: !!matched, item: matched };
  };

  const checkPackagingInInventory = (type, name, isFullset, itemId) => {
    if (!name || name === '—' || name === '-') return { inMaster: false, empty: true };
    if (name.toLowerCase().includes('dari client')) return { inMaster: true, isClient: true };

    const clean = name.trim().toLowerCase();
    const normalize = (str) => (str || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
    const normName = normalize(name);

    if (isFullset || type === 'fullset') {
      let found = itemId ? (fullsetListPrice.find(x => x.id === itemId) || botolList.find(x => x.id === itemId)) : null;
      if (!found) found = fullsetListPrice.find(x => (x.nama || '').trim().toLowerCase() === clean);
      if (!found) found = botolList.find(x => (x.nama || '').trim().toLowerCase() === clean);
      if (!found) found = fullsetListPrice.find(x => normalize(x.nama) === normName);
      if (!found) found = botolList.find(x => normalize(x.nama) === normName);
      
      // Fallback: If renamed (e.g. Hermes Press 55 ML -> Hermes Press 50 ML), match by common base name
      if (!found) {
        const rootName = clean.replace(/(\d+)\s*(ml|l|gr|g)?/gi, '').trim();
        if (rootName.length >= 4) {
          found = fullsetListPrice.find(x => (x.nama || '').toLowerCase().includes(rootName)) ||
                  botolList.find(x => (x.nama || '').toLowerCase().includes(rootName));
        }
      }

      return { inMaster: !!found, item: found };
    }
    if (type === 'botol') {
      let found = itemId ? (botolList.find(x => x.id === itemId) || fullsetListPrice.find(x => x.id === itemId)) : null;
      if (!found) found = botolList.find(x => (x.nama || '').trim().toLowerCase() === clean);
      if (!found) found = fullsetListPrice.find(x => (x.nama || '').trim().toLowerCase() === clean);
      if (!found) found = botolList.find(x => normalize(x.nama) === normName);
      if (!found) found = fullsetListPrice.find(x => normalize(x.nama) === normName);
      
      if (!found) {
        const rootName = clean.replace(/(\d+)\s*(ml|l|gr|g)?/gi, '').trim();
        if (rootName.length >= 4) {
          found = botolList.find(x => (x.nama || '').toLowerCase().includes(rootName)) ||
                  fullsetListPrice.find(x => (x.nama || '').toLowerCase().includes(rootName));
        }
      }

      return { inMaster: !!found, item: found };
    }
    if (type === 'tutup') {
      let found = itemId ? tutupList.find(x => x.id === itemId) : null;
      if (!found) found = tutupList.find(x => (x.nama || '').trim().toLowerCase() === clean);
      if (!found) found = tutupList.find(x => normalize(x.nama) === normName);
      return { inMaster: !!found, item: found };
    }
    if (type === 'spray') {
      let found = itemId ? sprayList.find(x => x.id === itemId) : null;
      if (!found) found = sprayList.find(x => (x.nama || '').trim().toLowerCase() === clean);
      if (!found) found = sprayList.find(x => normalize(x.nama) === normName);
      return { inMaster: !!found, item: found };
    }
    if (type === 'item_tambahan') {
      let found = itemId ? itemTambahanList.find(x => x.id === itemId) : null;
      if (!found) found = itemTambahanList.find(x => (x.nama || '').trim().toLowerCase() === clean);
      if (!found) found = itemTambahanList.find(x => normalize(x.nama) === normName);
      return { inMaster: !!found, item: found };
    }
    return { inMaster: false };
  };

  const formatStockNumber = (stok) => {
    if (stok === undefined || stok === null || isNaN(Number(stok))) return '0';
    const num = Number(stok);
    return Number.isInteger(num)
      ? num.toLocaleString('id-ID')
      : num.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const inventoryStatus = useMemo(() => {
    let missingCount = 0;
    let totalItems = 0;
    const missingItems = [];

    // Check Bahan Cair in formula
    if (sizeData.racikan?.racikan?.length) {
      sizeData.racikan.racikan.forEach(r => {
        totalItems++;
        const res = checkBahanInInventory(r);
        if (!res.inMaster && !res.isClient) {
          missingCount++;
          missingItems.push({ type: 'Bahan Cair', name: r.nama_bahan || r.bahan_nama || 'Bahan Cair' });
        }
      });
    }

    // Check Packaging in bahan
    if (sizeData.bahan) {
      if (sizeData.bahan.is_fullset) {
        if (sizeData.bahan.fullset_nama) {
          totalItems++;
          const res = checkPackagingInInventory('fullset', sizeData.bahan.fullset_nama, true);
          if (!res.inMaster && !res.isClient && !res.empty) {
            missingCount++;
            missingItems.push({ type: 'Kemasan Fullset', name: sizeData.bahan.fullset_nama });
          }
        }
      } else {
        if (sizeData.bahan.botol_nama) {
          totalItems++;
          const res = checkPackagingInInventory('botol', sizeData.bahan.botol_nama, false);
          if (!res.inMaster && !res.isClient && !res.empty) {
            missingCount++;
            missingItems.push({ type: 'Botol', name: sizeData.bahan.botol_nama });
          }
        }
        if (sizeData.bahan.tutup_nama) {
          totalItems++;
          const res = checkPackagingInInventory('tutup', sizeData.bahan.tutup_nama, false);
          if (!res.inMaster && !res.isClient && !res.empty) {
            missingCount++;
            missingItems.push({ type: 'Tutup', name: sizeData.bahan.tutup_nama });
          }
        }
        if (sizeData.bahan.spray_nama) {
          totalItems++;
          const res = checkPackagingInInventory('spray', sizeData.bahan.spray_nama, false);
          if (!res.inMaster && !res.isClient && !res.empty) {
            missingCount++;
            missingItems.push({ type: 'Spray / Pump', name: sizeData.bahan.spray_nama });
          }
        }
      }

      if (Array.isArray(sizeData.bahan.item_tambahan)) {
        sizeData.bahan.item_tambahan.forEach(it => {
          if (it.nama) {
            totalItems++;
            if (!it.dari_client) {
              const res = checkPackagingInInventory('item_tambahan', it.nama, false);
              if (!res.inMaster) {
                missingCount++;
                missingItems.push({ type: 'Item Tambahan', name: it.nama });
              }
            }
          }
        });
      }
    }

    return { missingCount, totalItems, missingItems };
  }, [sizeData, bahanList, botolList, tutupList, sprayList, fullsetListPrice, itemTambahanList]);

  return (
    <div className="bg-white border border-[#E6D5B8]/60 rounded-2xl overflow-hidden shadow-sm mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-white to-[#F9F5EC]/20 hover:bg-[#F9F5EC]/40 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#F9F5EC] border border-[#E6D5B8]/50 flex items-center justify-center">
            <span className="text-[#8C6208] font-black text-sm">{sizeData.size}</span>
          </div>
          <div className="text-left">
            <h3 className="font-bold text-slate-800 text-sm">Ukuran {sizeData.size}</h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {sizeData.racikan && <Badge variant="outline" className="text-[9px] bg-amber-50 text-[#8C6208] border-amber-200 font-bold">Formula</Badge>}
              {sizeData.bahan && <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200 font-bold">Kemasan</Badge>}
              {inventoryStatus.totalItems > 0 && (
                inventoryStatus.missingCount > 0 ? (
                  <Badge 
                    variant="outline" 
                    className="text-[9px] bg-rose-50 text-rose-700 border-rose-200 font-bold gap-1 cursor-help"
                    title={`Item belum terintegrasi:\n${inventoryStatus.missingItems.map(m => `• [${m.type}] ${m.name}`).join('\n')}`}
                  >
                    ⚠️ {inventoryStatus.missingCount} Blm Terintegrasi Inventori
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 font-bold gap-1">
                    ✓ Terintegrasi Inventori
                  </Badge>
                )
              )}
              {!sizeData.racikan && !sizeData.bahan && <span className="text-[10px] text-slate-400">Belum ada data</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Harga Jual Akhir Preview */}
          {(() => {
            const currentPreviewPrice = (sizeData.bahan?.custom_harga_jual !== undefined && sizeData.bahan?.custom_harga_jual !== null && sizeData.bahan?.custom_harga_jual !== '')
              ? Number(sizeData.bahan.custom_harga_jual)
              : ((sizeData.bahan?.custom_hpp !== undefined && sizeData.bahan?.custom_hpp !== null ? sizeData.bahan.custom_hpp : ((hppKemasan || 0) + (hppRacikanFinal || 0))) + (sizeData.bahan?.custom_jasa !== undefined && sizeData.bahan?.custom_jasa !== null ? sizeData.bahan.custom_jasa : defaultJasa) + (sizeData.bahan?.custom_margin || 0));

            const profitPreview = currentPreviewPrice - (modalFisikTotal || 0);
            const isDefisit = (modalFisikTotal > 0) && profitPreview < 0;
            const standardSystemTotal = ((hppKemasan || 0) + (hppRacikanFinal || 0)) + (sizeData.bahan?.custom_jasa !== undefined && sizeData.bahan?.custom_jasa !== null ? sizeData.bahan.custom_jasa : defaultJasa);
            const isDiBawahEstimasi = !isDefisit && (currentPreviewPrice < standardSystemTotal || (sizeData.bahan?.custom_margin !== undefined && Number(sizeData.bahan?.custom_margin) < 0));

            return (
              <div className="text-right hidden sm:block mr-2">
                <div className="flex items-center justify-end gap-1.5 mb-0.5">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Harga Jual / Pcs</p>
                  {isDefisit ? (
                    <span className="text-[8.5px] bg-rose-100 text-rose-800 border border-rose-200 px-1.5 py-0.2 rounded-full font-bold tracking-tight">
                      ● Defisit
                    </span>
                  ) : isDiBawahEstimasi ? (
                    <span className="text-[8.5px] bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.2 rounded-full font-bold tracking-tight">
                      ● Di Bawah Estimasi
                    </span>
                  ) : null}
                </div>
                <p className={`text-sm font-black transition-colors ${
                  isDefisit 
                    ? 'text-rose-600' 
                    : isDiBawahEstimasi 
                    ? 'text-amber-600' 
                    : 'text-emerald-700'
                }`}>
                  Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(currentPreviewPrice)}
                </p>
              </div>
            );
          })()}
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="w-8 h-8 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-full"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Yakin ingin menghapus ukuran ${sizeData.size} ini (termasuk formula & kemasannya)?`)) {
                    if (sizeData.racikan) onDeleteRacikan(sizeData.racikan.id);
                    if (sizeData.bahan) onDeleteBahan(sizeData.bahan.id);
                  }
                }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </button>

      {isOpen && (
        <div className="p-4 border-t border-[#E6D5B8]/40 space-y-6">
          {/* Banner Peringatan Item Belum Terintegrasi */}
          {inventoryStatus.missingCount > 0 && (
            <div className="bg-amber-50/90 border border-amber-200 rounded-xl p-3.5 flex items-start gap-3 shadow-2xs">
              <div className="w-7 h-7 rounded-lg bg-amber-100 border border-amber-300/60 flex items-center justify-center shrink-0 text-amber-700 font-bold text-sm">
                ⚠️
              </div>
              <div className="text-xs text-amber-900 space-y-1 flex-1">
                <p className="font-bold text-slate-800">
                  Ada {inventoryStatus.missingCount} item belum terintegrasi dengan Inventori:
                </p>
                <div className="flex flex-wrap gap-1.5 pt-0.5">
                  {inventoryStatus.missingItems.map((m, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 text-[10px] font-medium bg-white border border-amber-200 text-slate-800 px-2 py-0.5 rounded-md shadow-2xs">
                      <span className="text-amber-700 font-bold">[{m.type}]</span> {m.name}
                    </span>
                  ))}
                </div>
                <p className="text-[10px] text-slate-500 pt-0.5">
                  💡 <em>Tip: Daftarkan item di atas ke menu <strong>Stok & Inventori</strong> atau sesuaikan penulisan namanya agar stok dan HPP otomatis terhubung.</em>
                </p>
              </div>
            </div>
          )}

          {/* FORMULA CAIRAN */}
          <Card className="border border-slate-200 shadow-sm">
            <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <FlaskConical className="w-4 h-4 text-[#8C6208]" />
                <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider">Formula Cairan</h3>
              </div>
              <div className="flex gap-2">
                {canEdit && sizeData.racikan && (
                  <>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => onEditVariant(sizeData)}
                      className="text-xs text-[#8C6208] font-bold hover:bg-[#F9F5EC] gap-1 px-2.5 h-7 rounded-lg"
                    >
                      <Edit2 className="w-3 h-3" /> Edit
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={(e) => {
                        e.stopPropagation();
                        if(window.confirm('Yakin ingin menghapus Formula Cairan ini?')) {
                          onDeleteRacikan(sizeData.racikan.id);
                        }
                      }}
                      className="text-xs text-rose-600 font-bold hover:bg-rose-50 gap-1 px-2.5 h-7 rounded-lg z-10"
                    >
                      <Trash2 className="w-3 h-3" /> Hapus
                    </Button>
                  </>
                )}
              </div>
            </div>
            <CardContent className="p-4">
              {sizeData.racikan?.racikan?.length > 0 ? (
                <div className="rounded-xl border border-slate-100 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-slate-500 font-bold uppercase tracking-wider">Kategori</th>
                        <th className="text-left px-3 py-2 text-slate-500 font-bold uppercase tracking-wider">Vendor & Grade</th>
                        <th className="text-left px-3 py-2 text-slate-500 font-bold uppercase tracking-wider">Nama Bahan</th>
                        <th className="text-right px-3 py-2 text-slate-500 font-bold uppercase tracking-wider">%</th>
                        <th className="text-right px-3 py-2 text-slate-500 font-bold uppercase tracking-wider">Volume (ml)</th>
                        {isMarketing && <th className="text-right px-3 py-2 text-slate-500 font-bold uppercase tracking-wider">Harga Jual</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {sizeData.racikan.racikan.map((r, i) => {
                        const bottleSize = sizeData.racikan?.ukuran_ml || sizeData.bahan?.botol_ukuran_label_ml || 0;
                        const volMl = r.volume_ml || (bottleSize > 0 ? ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100) * bottleSize : 0);
                        const isLiter = needsLiterConversion('', r.kategori_bahan);
                        const gramMl = isLiter && volMl > 0 ? formatGramMl(volMl) : null;
                        
                        // Calculate harga jual for this ingredient
                        let hargaJualItem = 0;
                        if (isMarketing) {
                          const normalize = (str) => (str || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
                          let matchedBahan = bahanList.find(x => (x.nama || '').trim().toLowerCase() === (r.nama_bahan || '').trim().toLowerCase());
                          if (!matchedBahan) matchedBahan = bahanList.find(x => normalize(x.nama) === normalize(r.nama_bahan));
                          if (!matchedBahan) {
                            const stdCategories = ['dpg', 'aqua des', 'alkohol', 'peg', 'sustain'];
                            const catLower = (r.kategori_bahan || r.kategori || '').trim().toLowerCase();
                            if (stdCategories.includes(catLower)) matchedBahan = bahanList.find(x => (x.kategori || '').trim().toLowerCase() === catLower);
                          }
                          if (matchedBahan) {
                            const hjPerUnit = getHargaJual(matchedBahan); // per kg or liter
                            hargaJualItem = Math.round((hjPerUnit * volMl) / 1000); // convert to per-ml cost
                          }
                        }
                        
                        return (
                          <tr key={i} className="hover:bg-slate-50/50">
                            <td className="px-3 py-2.5 font-semibold text-[#8C6208]">{r.kategori_bahan || r.kategori}</td>
                            <td className="px-3 py-2.5 text-slate-500">
                              {r.vendor 
                                ? (r.vendor.toLowerCase() === 'client' ? 'Dari Client' : r.vendor)
                                : ((r.kategori_bahan || r.kategori) === 'Bibit' ? '—' : '—')}
                              {r.grade && <Badge className="ml-1 text-[9px] bg-amber-100 text-amber-700 font-bold border border-amber-200">{r.grade}</Badge>}
                            </td>
                            <td className="px-3 py-2.5 font-bold text-slate-800">
                              {(() => {
                                const namaBahan = r.nama_bahan || r.bahan_nama || '';
                                const checkBahan = checkBahanInInventory(r);
                                const displayName = checkBahan.item ? checkBahan.item.nama : namaBahan;

                                return (
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span>{displayName}</span>
                                    {checkBahan.isClient ? (
                                      <span className="inline-flex items-center gap-1 text-[9px] font-bold text-sky-700 bg-sky-50 border border-sky-200 px-1.5 py-0.5 rounded-md">
                                        Dari Client
                                      </span>
                                    ) : !checkBahan.inMaster ? (
                                      <span className="inline-flex items-center gap-1 text-[9px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-md" title="Bahan ini belum terintegrasi dengan Inventori">
                                        ⚠️ Blm Terintegrasi Inventori
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 text-[9px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-md" title={`Tersedia di Inventori (Stok: ${formatStockNumber(checkBahan.item?.stok)} ${checkBahan.item?.satuan || 'kg'})`}>
                                        ✓ Inventori ({formatStockNumber(checkBahan.item?.stok)} {checkBahan.item?.satuan || 'kg'})
                                      </span>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-3 py-2.5 text-right font-black text-slate-800">{r.persentase}%</td>
                            <td className="px-3 py-2.5 text-right text-slate-600 font-semibold">
                              {gramMl ? (
                                <span className="text-orange-600 font-bold">{gramMl.gramLabel} / {gramMl.mlLabel}</span>
                              ) : (
                                <span>{volMl > 0 ? volMl.toFixed(2) : 0} ml</span>
                              )}
                            </td>
                            {isMarketing && (
                              <td className="px-3 py-2.5 text-right font-bold text-emerald-700">
                                {hargaJualItem > 0 ? `Rp ${new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(hargaJualItem)}` : '—'}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6">
                  <FlaskConical className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-xs text-slate-400">Belum ada formula cairan terdaftar untuk ukuran ini.</p>
                  {canEdit && (
                    <Button 
                      onClick={() => onEditVariant(sizeData)}
                      size="sm" 
                      variant="outline" 
                      className="mt-3 text-xs border-[#E6D5B8] text-[#8C6208]"
                    >
                      Tambah Formula Cairan
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* KEMASAN / PACKAGING */}
          <Card className="border border-slate-200 shadow-sm">
            <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-slate-600" />
                <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider">Kemasan & Packaging</h3>
              </div>
              <div className="flex gap-2">
                {canEdit && sizeData.bahan && (
                  <>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => onEditVariant(sizeData)}
                      className="text-xs text-[#8C6208] font-bold hover:bg-[#F9F5EC] gap-1 px-2.5 h-7 rounded-lg"
                    >
                      <Edit2 className="w-3 h-3" /> Edit
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={(e) => {
                        e.stopPropagation();
                        if(window.confirm('Yakin ingin menghapus Kemasan & Packaging ini?')) {
                          onDeleteBahan(sizeData.bahan.id);
                        }
                      }}
                      className="text-xs text-rose-600 font-bold hover:bg-rose-50 gap-1 px-2.5 h-7 rounded-lg z-10"
                    >
                      <Trash2 className="w-3 h-3" /> Hapus
                    </Button>
                  </>
                )}
              </div>
            </div>
            <CardContent className="p-5 space-y-4">
              {sizeData.bahan ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Botol Utama */}
                    {(() => {
                      const isFullset = !!sizeData.bahan.is_fullset;
                      const botolName = isFullset && sizeData.bahan.fullset_nama ? sizeData.bahan.fullset_nama : (sizeData.bahan.botol_nama || '—');
                      const checkBotol = checkPackagingInInventory(isFullset ? 'fullset' : 'botol', botolName, isFullset);

                      return (
                        <div className="bg-[#FFFDF9] border border-[#E6D5B8]/40 rounded-xl p-3.5 shadow-xs flex flex-col justify-between">
                          <div>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Botol Utama</p>
                            <p className="text-xs font-black text-slate-800 mt-1">{botolName}</p>
                            {sizeData.bahan.botol_ukuran_label_ml && (
                              <p className="text-[10px] text-slate-500 mt-0.5">
                                Label: {sizeData.bahan.botol_ukuran_label_ml}ml
                                {sizeData.bahan.botol_ukuran_aktual_ml && sizeData.bahan.botol_ukuran_aktual_ml !== sizeData.bahan.botol_ukuran_label_ml
                                  ? ` (Aktual: ${sizeData.bahan.botol_ukuran_aktual_ml}ml)` : ''}
                              </p>
                            )}
                          </div>
                          <div className="mt-2">
                            {checkBotol.isClient ? (
                              <span className="inline-flex items-center gap-1 text-[9px] font-bold text-sky-700 bg-sky-50 border border-sky-200 px-1.5 py-0.5 rounded-md">
                                Dari Client
                              </span>
                            ) : !checkBotol.inMaster && !checkBotol.empty ? (
                              <span className="inline-flex items-center gap-1 text-[9px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-md">
                                ⚠️ Blm Terintegrasi Inventori
                              </span>
                            ) : checkBotol.inMaster ? (
                              <span className="inline-flex items-center gap-1 text-[9px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-md">
                                ✓ Inventori ({formatStockNumber(checkBotol.item?.stok)} pcs)
                              </span>
                            ) : null}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Tutup */}
                    {(() => {
                      const isFullset = !!sizeData.bahan.is_fullset;
                      const tutupName = isFullset ? 'Satu set dengan botol (Fullset)' : (sizeData.bahan.tutup_nama || 'Tutup Dari Client');
                      const checkTutup = isFullset ? { inMaster: true } : checkPackagingInInventory('tutup', tutupName, false);

                      return (
                        <div className="bg-[#FFFDF9] border border-[#E6D5B8]/40 rounded-xl p-3.5 shadow-xs flex flex-col justify-between">
                          <div>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Tutup</p>
                            <p className="text-xs font-black text-slate-800 mt-1">{tutupName}</p>
                          </div>
                          <div className="mt-2">
                            {isFullset ? (
                              <span className="inline-flex items-center gap-1 text-[9px] font-medium text-slate-600 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded-md">
                                Inklusi Fullset
                              </span>
                            ) : checkTutup.isClient ? (
                              <span className="inline-flex items-center gap-1 text-[9px] font-bold text-sky-700 bg-sky-50 border border-sky-200 px-1.5 py-0.5 rounded-md">
                                Dari Client
                              </span>
                            ) : !checkTutup.inMaster && !checkTutup.empty ? (
                              <span className="inline-flex items-center gap-1 text-[9px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-md">
                                ⚠️ Blm Terintegrasi Inventori
                              </span>
                            ) : checkTutup.inMaster ? (
                              <span className="inline-flex items-center gap-1 text-[9px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-md">
                                ✓ Inventori ({formatStockNumber(checkTutup.item?.stok)} pcs)
                              </span>
                            ) : null}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Spray / Pump */}
                    {(() => {
                      const isFullset = !!sizeData.bahan.is_fullset;
                      const sprayName = isFullset ? 'Satu set dengan botol (Fullset)' : (sizeData.bahan.spray_nama || 'Spray Dari Client');
                      const checkSpray = isFullset ? { inMaster: true } : checkPackagingInInventory('spray', sprayName, false);

                      return (
                        <div className="bg-[#FFFDF9] border border-[#E6D5B8]/40 rounded-xl p-3.5 shadow-xs flex flex-col justify-between">
                          <div>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Spray / Pump</p>
                            <p className="text-xs font-black text-slate-800 mt-1">{sprayName}</p>
                          </div>
                          <div className="mt-2">
                            {isFullset ? (
                              <span className="inline-flex items-center gap-1 text-[9px] font-medium text-slate-600 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded-md">
                                Inklusi Fullset
                              </span>
                            ) : checkSpray.isClient ? (
                              <span className="inline-flex items-center gap-1 text-[9px] font-bold text-sky-700 bg-sky-50 border border-sky-200 px-1.5 py-0.5 rounded-md">
                                Dari Client
                              </span>
                            ) : !checkSpray.inMaster && !checkSpray.empty ? (
                              <span className="inline-flex items-center gap-1 text-[9px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-md">
                                ⚠️ Blm Terintegrasi Inventori
                              </span>
                            ) : checkSpray.inMaster ? (
                              <span className="inline-flex items-center gap-1 text-[9px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-md">
                                ✓ Inventori ({formatStockNumber(checkSpray.item?.stok)} pcs)
                              </span>
                            ) : null}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Item Tambahan</p>
                    {sizeData.bahan.item_tambahan?.length > 0 ? (
                      <div className="rounded-xl border border-slate-100 overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left px-3 py-2 text-slate-500 font-bold uppercase tracking-wider">Nama Item</th>
                              <th className="text-left px-3 py-2 text-slate-500 font-bold uppercase tracking-wider">Kategori</th>
                              <th className="text-right px-3 py-2 text-slate-500 font-bold uppercase tracking-wider">Qty/pcs</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {sizeData.bahan.item_tambahan.map((it, idx) => {
                              const checkItem = checkPackagingInInventory('item_tambahan', it.nama, false);

                              return (
                                <tr key={idx} className="hover:bg-slate-50/50">
                                  <td className="px-3 py-2.5 font-bold text-slate-800">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span>{it.nama}</span>
                                      {it.dari_client ? (
                                        <Badge variant="outline" className="text-[9px] bg-sky-50 text-sky-700 border-sky-200">
                                          Dari Client
                                        </Badge>
                                      ) : !checkItem.inMaster ? (
                                        <span className="inline-flex items-center gap-1 text-[9px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-md">
                                          ⚠️ Blm Terintegrasi Inventori
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 text-[9px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-md">
                                          ✓ Inventori ({formatStockNumber(checkItem.item?.stok)} pcs)
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2.5"><Badge variant="outline" className="text-[10px] font-bold">{it.kategori}</Badge></td>
                                  <td className="px-3 py-2.5 text-right font-black text-slate-800">{it.qty_per_pcs}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-xs font-black text-slate-400 bg-slate-50 border border-slate-100 border-dashed rounded-lg p-3 text-center">Tidak ada item tambahan (Dus, stiker, dsb)</p>
                    )}
                  </div>

                  <div className="pt-2">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Desain Artwork</p>
                      {canEdit && (
                        <div>
                          <input 
                            type="file" 
                            accept="image/*" 
                            className="hidden" 
                            ref={fileRef} 
                            onChange={handleDirectUpload} 
                          />
                          <Button 
                            size="sm" 
                            variant="outline"
                            disabled={uploadingImage}
                            onClick={() => fileRef.current?.click()}
                            className="h-6 text-[10px] border-[#E6D5B8] text-[#8C6208] hover:bg-[#F9F5EC]"
                          >
                            {uploadingImage ? 'Mengunggah...' : (sizeData.bahan.image_url ? 'Ganti Gambar' : 'Unggah Gambar')}
                          </Button>
                        </div>
                      )}
                    </div>
                    <ClickToPreviewImage src={sizeData.bahan.image_url} alt={`Artwork ${activeBrandName} ${variantName}`} />
                  </div>
                </>
              ) : (
                <div className="text-center py-6">
                  <Package className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-xs text-slate-400">Belum ada data kemasan terdaftar untuk ukuran ini.</p>
                  {canEdit && (
                    <Button 
                      onClick={() => onEditVariant(sizeData)}
                      size="sm" 
                      variant="outline" 
                      className="mt-3 text-xs border-[#E6D5B8] text-[#8C6208]"
                    >
                      Tambah Data Kemasan
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ESTIMASI & PENENTUAN HARGA (Khusus Marketing) */}
          {isMarketing && (() => {
            const marginKemasan = Math.max(0, (hppKemasanUtama || 0) - (modalKemasanUtama || 0));
            const marginCairan = Math.max(0, (hppRacikanFinal || 0) - (modalRacikanFinal || 0));
            const marginTambahan = Math.max(0, (hppItemTambahan || 0) - (modalItemTambahan || 0));
            const customMarginVal = sizeData.bahan?.custom_margin !== undefined && sizeData.bahan?.custom_margin !== null && sizeData.bahan?.custom_margin !== '' ? Number(sizeData.bahan.custom_margin) : 0;
            const currentJasa = sizeData.bahan?.custom_jasa !== undefined && sizeData.bahan?.custom_jasa !== null ? Number(sizeData.bahan.custom_jasa) : defaultJasa;
            const hargaSistemBahan = (hppKemasan || 0) + (hppRacikanFinal || 0);
            const totalSetelahJasa = hargaSistemBahan + currentJasa;
            const currentHargaJualAkhir = (sizeData.bahan?.custom_harga_jual !== undefined && sizeData.bahan?.custom_harga_jual !== null && sizeData.bahan?.custom_harga_jual !== '')
              ? Number(sizeData.bahan.custom_harga_jual)
              : (totalSetelahJasa + customMarginVal);
            const totalProfit = currentHargaJualAkhir - modalFisikTotal;
            const isBoncos = totalProfit < 0;
            const isUnderSystem = !isBoncos && (currentHargaJualAkhir < totalSetelahJasa || customMarginVal < 0);

            return (
              <Card className={`border shadow-sm mt-4 transition-all ${
                isBoncos 
                  ? 'border-rose-300 bg-rose-50/40' 
                  : isUnderSystem 
                  ? 'border-amber-300 bg-amber-50/30' 
                  : 'border-emerald-200 bg-emerald-50/30'
              }`}>
                <div className="p-4 border-b border-slate-100 flex items-center gap-2 bg-white rounded-t-xl">
                  <Banknote className={`w-5 h-5 ${isBoncos ? 'text-rose-600' : isUnderSystem ? 'text-amber-600' : 'text-emerald-700'}`} />
                  <h3 className={`text-xs font-black uppercase tracking-wider ${isBoncos ? 'text-rose-800' : isUnderSystem ? 'text-amber-800' : 'text-emerald-800'}`}>
                    Manajemen Harga & Jasa (Marketing)
                  </h3>
                </div>
                <CardContent className="p-5 bg-white rounded-b-xl space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    
                    {/* 1. Harga Sistem Sebelum Jasa */}
                    <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm hover:border-slate-300 transition-colors">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-bold text-slate-500">Harga Sistem (Sebelum Jasa)</span>
                        <span className="text-[9px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded whitespace-nowrap">Dasar Sistem</span>
                      </div>
                      <div className="h-10 flex items-center px-3 w-full bg-emerald-50 border border-emerald-200 rounded-md">
                        <span className="text-base font-black text-emerald-700 whitespace-nowrap tracking-tight">
                          Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(hargaSistemBahan)}
                        </span>
                      </div>
                      
                      <div className="mt-2.5 pt-2 border-t border-slate-100 space-y-1.5 text-[10px]">
                        <div>
                          <span className="text-slate-400 font-medium block text-[9.5px] mb-0.5">Rincian Harga Jual Bahan:</span>
                          <p className="text-slate-600 flex flex-wrap gap-x-1.5 items-center leading-relaxed">
                            <span>Kemasan: <strong className="text-slate-800 font-bold">Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(hppKemasanUtama || 0)}</strong></span>
                            <span className="text-slate-300">·</span>
                            <span>Cairan: <strong className="text-slate-800 font-bold">Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(hppRacikanFinal || 0)}</strong></span>
                            {hppItemTambahan > 0 && (
                              <>
                                <span className="text-slate-300">·</span>
                                <span>Tambahan: <strong className="text-slate-800 font-bold">Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(hppItemTambahan || 0)}</strong></span>
                              </>
                            )}
                          </p>
                        </div>

                        <div className="pt-1.5 border-t border-slate-100">
                          <div className="flex justify-between text-slate-500 mb-0.5">
                            <span className="text-[9.5px]">Total Modal Beli:</span>
                            <strong className="text-slate-700 font-bold">Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(modalFisikTotal || 0)}</strong>
                          </div>
                          <p className="text-[9px] text-slate-400 flex flex-wrap gap-x-1.5">
                            <span>K: Rp {new Intl.NumberFormat('id-ID').format(modalKemasanUtama || 0)}</span>·
                            <span>C: Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(modalRacikanFinal || 0)}</span>·
                            <span>T: Rp {new Intl.NumberFormat('id-ID').format(modalItemTambahan || 0)}</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* 2. Harga Jasa Produksi & Jumlah Setelah Jasa */}
                    <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm hover:border-slate-300 transition-colors">
                      <p className="text-xs font-bold text-slate-500 mb-2">Harga Jasa Produksi</p>
                      {editingJasa ? (
                        <div className="flex items-center gap-2 mb-2">
                          <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 font-bold">Rp</span>
                            <input 
                              type="text"
                              value={customJasa ? new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(customJasa) : ''}
                              onChange={e => {
                                const val = e.target.value.replace(/\D/g, '');
                                setCustomJasa(val ? Number(val) : '');
                              }}
                              className="w-full pl-9 pr-3 py-1.5 text-sm font-bold text-slate-800 border-2 border-emerald-500 rounded-lg focus:outline-none focus:ring-0"
                              placeholder={defaultJasa}
                              autoFocus
                              onKeyDown={e => {
                                if(e.key === 'Enter') handleSaveJasa();
                                if(e.key === 'Escape') { setCustomJasa(sizeData.bahan?.custom_jasa || ''); setEditingJasa(false); }
                              }}
                            />
                          </div>
                          <Button 
                            size="icon" 
                            className="h-8 w-8 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shrink-0" 
                            onClick={handleSaveJasa}
                            disabled={isSavingJasa}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost"
                            className="h-8 w-8 text-slate-400 hover:text-slate-600 rounded-lg shrink-0" 
                            onClick={() => {
                              setCustomJasa(sizeData.bahan?.custom_jasa || '');
                              setEditingJasa(false);
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="group flex items-center gap-3 relative">
                          {sizeData.bahan?.custom_jasa !== undefined && sizeData.bahan?.custom_jasa !== null ? (
                            <div className="h-10 flex items-center px-3 w-full bg-emerald-50 border border-emerald-200 rounded-md">
                              <span className="text-base font-black text-emerald-700 whitespace-nowrap">Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(sizeData.bahan.custom_jasa)}</span>
                            </div>
                          ) : (
                            <div className="h-10 flex items-center px-3 w-full bg-slate-50 border border-slate-200 rounded-md">
                              <span className="text-base font-black text-slate-700 whitespace-nowrap">Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(defaultJasa)}</span>
                            </div>
                          )}
                          <button 
                            onClick={() => setEditingJasa(true)}
                            className="p-1.5 text-slate-300 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-all opacity-0 group-hover:opacity-100 absolute right-6"
                            title="Edit Harga Jasa"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      <div className="mt-2.5 pt-2 border-t border-slate-100 space-y-1 text-[10px]">
                        <span className="text-slate-400 font-medium block text-[9.5px]">Jumlah Setelah + Jasa:</span>
                        <div className="h-9 px-3 rounded-md bg-emerald-50/70 border border-emerald-200 flex items-center justify-between">
                          <span className="text-sm font-black text-emerald-800">
                            Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalSetelahJasa)}
                          </span>
                          <span className="text-[9px] bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.5 rounded">
                            Bahan + Jasa
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* 3. Margin / Keuntungan */}
                    <div className={`bg-white border p-4 rounded-xl shadow-sm transition-all ${
                      isBoncos 
                        ? 'border-rose-300 bg-rose-50/20' 
                        : isUnderSystem 
                        ? 'border-amber-300 bg-amber-50/20' 
                        : 'border-emerald-200'
                    }`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-bold text-slate-500">Margin Tambahan</span>
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${
                          isBoncos ? 'text-rose-700 bg-rose-100' : isUnderSystem ? 'text-amber-700 bg-amber-100' : 'text-emerald-600 bg-emerald-50'
                        }`}>
                          {isBoncos ? 'Di Bawah Modal' : isUnderSystem ? 'Di Bawah Rekomendasi' : 'Terkunci'}
                        </span>
                      </div>
                      <div className={`h-10 flex items-center px-3 w-full rounded-md border ${
                        isBoncos ? 'bg-rose-50/50 border-rose-200' : isUnderSystem ? 'bg-amber-50/50 border-amber-200' : 'bg-emerald-50/50 border-emerald-200'
                      }`}>
                        <span className={`text-base font-bold whitespace-nowrap ${
                          isBoncos ? 'text-rose-800' : isUnderSystem ? 'text-amber-800' : 'text-emerald-800'
                        }`}>
                          {customMarginVal < 0 
                            ? `- Rp ${new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(customMarginVal))}` 
                            : `Rp ${new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(customMarginVal)}`}
                        </span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] space-y-0.5 text-slate-500">
                        <div className="flex justify-between text-[9.5px]">
                          <span>Margin Kemasan:</span>
                          <span>+ Rp {new Intl.NumberFormat('id-ID').format(marginKemasan)}</span>
                        </div>
                        <div className="flex justify-between text-[9.5px]">
                          <span>Margin Cairan:</span>
                          <span>+ Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(marginCairan)}</span>
                        </div>
                        <div className={`flex justify-between font-bold pt-1 border-t ${
                          isBoncos ? 'border-rose-200 text-rose-700' : isUnderSystem ? 'border-amber-200 text-amber-700' : 'border-emerald-100 text-emerald-700'
                        }`}>
                          <span>Total Keuntungan Bersih:</span>
                          <span>
                            {totalProfit < 0 ? `- Rp ${new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(totalProfit))}` : `Rp ${new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalProfit)}`}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Harga Jual Akhir */}
                    <div className={`p-4 rounded-xl shadow-sm md:col-span-3 border-2 transition-all ${
                      isBoncos 
                        ? 'bg-rose-50/60 border-rose-400' 
                        : isUnderSystem 
                        ? 'bg-amber-50/50 border-amber-400' 
                        : 'bg-gradient-to-r from-emerald-50/40 via-white to-emerald-50/40 border-emerald-300'
                    }`}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <label className={`text-sm font-black block flex items-center gap-2 ${
                            isBoncos ? 'text-rose-900' : isUnderSystem ? 'text-amber-900' : 'text-emerald-900'
                          }`}>
                            Harga Jual Akhir (Ditagihkan ke Klien)
                            {isBoncos ? (
                              <span className="text-[10px] bg-rose-600 text-white px-2 py-0.5 rounded-full font-bold tracking-tight">
                                ● Defisit Modal Fisik
                              </span>
                            ) : isUnderSystem ? (
                              <span className="text-[10px] bg-amber-600 text-white px-2 py-0.5 rounded-full font-bold tracking-tight">
                                ● Di Bawah Estimasi Sistem
                              </span>
                            ) : (
                              <span className="text-[10px] bg-emerald-600 text-white px-2 py-0.5 rounded-full font-bold tracking-tight">
                                ● Margin Terkunci
                              </span>
                            )}
                          </label>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            {isBoncos ? (
                              <strong className="text-rose-700 font-semibold">
                                Estimasi harga jual berada di bawah modal pokok fisik (Rp {modalFisikTotal.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}). Terdapat potensi defisit Rp {Math.abs(totalProfit).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per pcs.
                              </strong>
                            ) : isUnderSystem ? (
                              <strong className="text-amber-700 font-semibold">
                                Harga disesuaikan di bawah estimasi standar sistem (Rp {totalSetelahJasa.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}), proyeksi margin bersih Rp {totalProfit.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per pcs.
                              </strong>
                            ) : (
                              'Harga jual yang disepakati dengan klien. Margin otomatis terkunci untuk Repeat Order agar proyeksi biaya tetap stabil.'
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {editingHargaJual ? (
                            <div className="flex items-center gap-2">
                              <div className="relative w-52">
                                <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold ${
                                  isBoncos ? 'text-rose-700' : isUnderSystem ? 'text-amber-700' : 'text-emerald-700'
                                }`}>Rp</span>
                                <input 
                                  type="text"
                                  value={customHargaJual ? new Intl.NumberFormat('id-ID').format(customHargaJual) : ''}
                                  onChange={e => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    setCustomHargaJual(val ? Number(val) : '');
                                  }}
                                  className={`w-full pl-9 pr-3 py-2 text-base font-black border-2 rounded-xl focus:outline-none focus:ring-0 ${
                                    isBoncos 
                                      ? 'border-rose-500 text-rose-900 bg-rose-50/30' 
                                      : isUnderSystem 
                                      ? 'border-amber-500 text-amber-900 bg-amber-50/20' 
                                      : 'border-emerald-500 text-emerald-900 bg-white'
                                  }`}
                                  placeholder={new Intl.NumberFormat('id-ID').format(Math.round(defaultHargaTotal))}
                                  autoFocus
                                  onKeyDown={e => {
                                    if(e.key === 'Enter') handleSaveHargaJual();
                                    if(e.key === 'Escape') { setCustomHargaJual(sizeData.bahan?.custom_harga_jual || ''); setEditingHargaJual(false); }
                                  }}
                                />
                              </div>
                              <Button 
                                size="icon" 
                                className={`h-9 w-9 text-white rounded-lg shrink-0 ${
                                  isBoncos ? 'bg-rose-600 hover:bg-rose-700' : isUnderSystem ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'
                                }`} 
                                onClick={handleSaveHargaJual}
                                disabled={isSavingHargaJual}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="icon" 
                                variant="ghost"
                                className="h-9 w-9 text-slate-400 hover:text-slate-600 rounded-lg shrink-0" 
                                onClick={() => {
                                  setCustomMargin(sizeData.bahan?.custom_margin ?? '');
                                  setEditingHargaJual(false);
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 shrink-0">
                              <div className={`h-11 px-4 flex items-center border-2 rounded-xl shadow-xs whitespace-nowrap ${
                                isBoncos ? 'bg-rose-50 border-rose-500' : isUnderSystem ? 'bg-amber-50 border-amber-500' : 'bg-white border-emerald-500'
                              }`}>
                                <span className={`text-base sm:text-lg font-black tracking-tight whitespace-nowrap ${
                                  isBoncos ? 'text-rose-900' : isUnderSystem ? 'text-amber-900' : 'text-emerald-900'
                                }`}>
                                  Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(currentHargaJualAkhir)}
                                </span>
                              </div>
                              {canEdit && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setCustomHargaJual(currentHargaJualAkhir);
                                    setEditingHargaJual(true);
                                  }}
                                  className={`h-11 text-xs font-bold rounded-xl gap-1.5 ${
                                    isBoncos 
                                      ? 'border-rose-300 text-rose-700 hover:bg-rose-50' 
                                      : isUnderSystem 
                                      ? 'border-amber-300 text-amber-700 hover:bg-amber-50' 
                                      : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                                  }`}
                                >
                                  <Edit2 className="w-3.5 h-3.5" /> Ubah
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })()}
        </div>
      )}
    </div>
  );
}
